import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface LogEntry {
  timestamp: string;
  deviceMac: string;
  deviceName: string;
  scheduleName: string;
  previousDominantGateway: string;
  newDominantGateway: string;
  previousDominancePercentage: number;
  newDominancePercentage: number;
  previousPosition: { x: number; y: number };
  newPosition: { x: number; y: number };
  positionChangeDistance: number;
  positionChangePercentage: number;
  signalHistorySnapshot: Array<{
    gateway: string;
    rssi: number;
    timestamp: string;
  }>;
  allGatewaysAtTime: Array<{
    gateway: string;
    avgRssi: number;
    signalCount: number;
  }>;
}

interface LogViewerProps {
  password: string;
}

const LogViewer: React.FC<LogViewerProps> = ({ password }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    startTime: "",
    endTime: "",
    deviceMac: "",
    scheduleName: "",
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.startTime) params.append("startTime", filters.startTime);
      if (filters.endTime) params.append("endTime", filters.endTime);
      if (filters.deviceMac) params.append("deviceMac", filters.deviceMac);
      if (filters.scheduleName)
        params.append("scheduleName", filters.scheduleName);

      const response = await axios.get(
        `${API_BASE_URL}/api/dominant-monitoring/logs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setLogs(response.data.logs);
      }
    } catch (err: any) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/dominant-monitoring/export`,
        filters,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `dominant-gateway-logs-${filters.startDate}-to-${filters.endDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error("Failed to export logs:", err);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">📋 Anomaly Logs</h2>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          title="Refresh logs"
        >
          <svg
            className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Time
            </label>
            <input
              type="time"
              value={filters.startTime}
              onChange={(e) =>
                setFilters({ ...filters, startTime: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Time
            </label>
            <input
              type="time"
              value={filters.endTime}
              onChange={(e) =>
                setFilters({ ...filters, endTime: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device MAC
            </label>
            <input
              type="text"
              value={filters.deviceMac}
              onChange={(e) =>
                setFilters({ ...filters, deviceMac: e.target.value })
              }
              placeholder="Filter by MAC"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule
            </label>
            <input
              type="text"
              value={filters.scheduleName}
              onChange={(e) =>
                setFilters({ ...filters, scheduleName: e.target.value })
              }
              placeholder="Filter by schedule"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
          >
            {loading ? "Loading..." : "Apply Filters"}
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No logs found for the selected filters.</p>
            <p className="text-sm mt-1">
              Logs will appear here when dominant gateway changes occur during
              configured schedules.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Device
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Schedule
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Gateway Change
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Position Δ
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{log.deviceName}</div>
                    <div className="text-xs text-gray-500">{log.deviceMac}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.scheduleName}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-red-600">
                        {log.previousDominantGateway}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-600">
                        {log.newDominantGateway}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{log.positionChangeDistance}px</div>
                    <div className="text-xs text-gray-500">
                      {log.positionChangePercentage}%
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  🔍 Anomaly Details
                </h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Device</p>
                    <p className="text-lg font-medium">
                      {selectedLog.deviceName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedLog.deviceMac}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="text-lg font-medium">
                      {formatTimestamp(selectedLog.timestamp)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Schedule</p>
                    <p className="text-lg font-medium">
                      {selectedLog.scheduleName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Position Change</p>
                    <p className="text-lg font-medium">
                      {selectedLog.positionChangeDistance}px (
                      {selectedLog.positionChangePercentage}%)
                    </p>
                  </div>
                </div>

                {/* Gateway Change */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Gateway Change
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Previous</p>
                      <p className="text-lg font-medium text-red-700">
                        {selectedLog.previousDominantGateway}
                      </p>
                      <p className="text-sm text-gray-600">
                        Dominance: {selectedLog.previousDominancePercentage}%
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">New</p>
                      <p className="text-lg font-medium text-green-700">
                        {selectedLog.newDominantGateway}
                      </p>
                      <p className="text-sm text-gray-600">
                        Dominance: {selectedLog.newDominancePercentage}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Position Change */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Position Change
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Previous</p>
                      <p className="text-lg font-medium">
                        ({selectedLog.previousPosition.x},{" "}
                        {selectedLog.previousPosition.y})
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">New</p>
                      <p className="text-lg font-medium">
                        ({selectedLog.newPosition.x},{" "}
                        {selectedLog.newPosition.y})
                      </p>
                    </div>
                  </div>
                </div>

                {/* All Gateways at Time */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    All Gateways at Time of Change
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedLog.allGatewaysAtTime.map((gw, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <span className="font-medium">{gw.gateway}</span>
                        <div className="text-sm text-gray-600">
                          <span>Avg RSSI: {gw.avgRssi}dBm</span>
                          <span className="ml-3">
                            Signals: {gw.signalCount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Signal History */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Signal History (All{" "}
                    {selectedLog.signalHistorySnapshot.length} entries)
                  </h4>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {selectedLog.signalHistorySnapshot.map((signal, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                      >
                        <span className="font-medium">{signal.gateway}</span>
                        <div className="text-gray-600">
                          <span>RSSI: {signal.rssi}dBm</span>
                          <span className="ml-3 text-xs">
                            {new Date(signal.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-6 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-600">
        Showing {logs.length} log{logs.length !== 1 ? "s" : ""} from{" "}
        {filters.startDate} to {filters.endDate}
      </div>
    </div>
  );
};

export default LogViewer;
